
import React, { useState } from 'react';
import { Check, Star, Zap, Shield, Target, TrendingUp, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface EnhancedBotFeaturesSectionProps {
  features: string[] | undefined;
  isLoading: boolean;
}

const FeatureItem = ({ 
  feature, 
  index, 
  icon: Icon, 
  isMobile = false 
}: { 
  feature: string; 
  index: number; 
  icon: any; 
  isMobile?: boolean; 
}) => {
  return (
    <div 
      className="group relative flex items-start gap-4 p-4 rounded-xl border border-border/50 bg-gradient-to-r from-background via-muted/10 to-background transition-all duration-500 hover:shadow-lg hover:border-border hover:-translate-y-1 animate-fade-in"
      style={{ animationDelay: `${index * 150}ms` }}
    >
      {/* Timeline connector */}
      {index > 0 && (
        <div className="absolute -top-4 left-6 w-0.5 h-4 bg-gradient-to-b from-primary/30 to-transparent" />
      )}
      
      {/* Animated icon */}
      <div className="flex-shrink-0 mt-1">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative w-10 h-10 bg-gradient-to-br from-green-500/20 to-green-500/10 rounded-full flex items-center justify-center border border-green-500/20 transition-all duration-300 group-hover:scale-110 group-hover:border-green-500/40">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <Icon className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-green-500 transition-transform duration-300 group-hover:rotate-12 relative z-10`} />
          </div>
        </div>
      </div>
      
      {/* Feature content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className={`${isMobile ? "text-sm" : "text-base"} font-medium leading-relaxed transition-colors duration-300 group-hover:text-foreground`}>
            {feature}
          </span>
          
          {/* Animated checkmark */}
          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
            <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
              <Check className="h-3 w-3 text-white" />
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full transition-all duration-1000 group-hover:scale-x-100 origin-left scale-x-0"
            style={{ transitionDelay: `${index * 100}ms` }}
          />
        </div>
      </div>
    </div>
  );
};

const FeatureCategory = ({ 
  title, 
  features, 
  icon: Icon, 
  isExpanded, 
  onToggle, 
  isMobile = false 
}: {
  title: string;
  features: string[];
  icon: any;
  isExpanded: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}) => {
  return (
    <div className="space-y-3">
      <Button
        variant="ghost"
        onClick={onToggle}
        className="w-full justify-between p-3 h-auto hover:bg-muted/50 transition-all duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg flex items-center justify-center">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          <span className={`${isMobile ? "text-sm" : "text-base"} font-medium`}>{title}</span>
          <span className="text-xs text-muted-foreground">({features.length})</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 transition-transform duration-200" />
        ) : (
          <ChevronRight className="h-4 w-4 transition-transform duration-200" />
        )}
      </Button>
      
      {isExpanded && (
        <div className="space-y-3 pl-4 border-l-2 border-primary/20">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              feature={feature}
              index={index}
              icon={Check}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const EnhancedBotFeaturesSection = ({
  features,
  isLoading
}: EnhancedBotFeaturesSectionProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['core']));

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-background to-blue-500/5" />
        <CardHeader className={`${isMobile ? "p-4" : "p-6"} pb-4 relative`}>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className={`${isMobile ? "px-4 pb-4" : "px-6 pb-6"} relative`}>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayFeatures = features && features.length > 0
    ? features
    : [
        t("default_feature_automated"),
        t("default_feature_risk"),
        t("default_feature_analysis"),
        t("default_feature_reporting")
      ];

  // Icon rotation for features
  const featureIcons = [Star, Zap, Shield, Target, TrendingUp, Check];

  // Categorize features (simplified for demo)
  const categorizedFeatures = {
    core: displayFeatures.slice(0, Math.ceil(displayFeatures.length / 2)),
    advanced: displayFeatures.slice(Math.ceil(displayFeatures.length / 2))
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <Card className="relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-background to-blue-500/5" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-full blur-2xl" />
      
      <CardHeader className={`${isMobile ? "p-4" : "p-6"} pb-4 relative`}>
        <CardTitle className={`${isMobile ? "text-lg" : "text-xl"} flex items-center gap-3`}>
          <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-blue-500 rounded-full" />
          {t('bot_features')}
          <div className="ml-auto text-sm font-normal text-muted-foreground">
            {displayFeatures.length} {t('features_available')}
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className={`${isMobile ? "px-4 pb-4" : "px-6 pb-6"} relative`}>
        {displayFeatures.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-muted/50 rounded-full flex items-center justify-center">
              <Star className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">{t('no_features_available')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Core Features */}
            <FeatureCategory
              title={t('core_features')}
              features={categorizedFeatures.core}
              icon={Shield}
              isExpanded={expandedCategories.has('core')}
              onToggle={() => toggleCategory('core')}
              isMobile={isMobile}
            />
            
            {/* Advanced Features */}
            {categorizedFeatures.advanced.length > 0 && (
              <FeatureCategory
                title={t('advanced_features')}
                features={categorizedFeatures.advanced}
                icon={Star}
                isExpanded={expandedCategories.has('advanced')}
                onToggle={() => toggleCategory('advanced')}
                isMobile={isMobile}
              />
            )}
            
            {/* Simple list fallback for smaller feature sets */}
            {displayFeatures.length <= 3 && (
              <div className="space-y-3">
                {displayFeatures.map((feature, index) => {
                  const IconComponent = featureIcons[index % featureIcons.length];
                  return (
                    <FeatureItem
                      key={index}
                      feature={feature}
                      index={index}
                      icon={IconComponent}
                      isMobile={isMobile}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Feature completion indicator */}
        {displayFeatures.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border/30">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-full animate-pulse" />
              <span>{t('all_features_verified')}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
